import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import useApi from "../../../CustomHooks/useApi";
import MerchantShTxnHistory from "../MerchantShTxnHistory/MerchantShTxnHistory";
import Modal from "../../../ResuableCompoment/Modal";
import * as XLSX from "xlsx";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

/* ============================= */
/*   NEW HIERARCHY HEADINGS      */
/* ============================= */

const HIERARCHY_COLUMNS = [
    "SH",
    "ASM",
    "Master Distributor",
    "Distributor",
    "Retailer",
    "Independent Retailer",
];

const HierarchyShshow = () => {
    /* ============================= */
    /*        DATE FUNCTIONS         */
    /* ============================= */

    const formatDate = (d) => d.toISOString().split("T")[0];

    const getToday = () => formatDate(new Date());


    const rootMobile = useSelector(
        (state) => state.auth.employeeDetails?.mobile_number
    );

    const [fromDate, setFromDate] = useState(getToday());
    const [toDate, setToDate] = useState(getToday());

    const MIN_DATE = "";
    const MAX_DATE = getToday();

    const { callApi: hierarchy } = useApi("/hierarchy/v2/list", "GET");
    const { callApi: searchHierarchy } = useApi("/hierarchy/v2/search", "GET");

    const [currentMobile, setCurrentMobile] = useState(null);
    const [hierarchyData, setHierarchyData] = useState([]);
    const [path, setPath] = useState([]);
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const [selectedMid, setSelectedMid] = useState(null);
    const [selectedService, setSelectedService] = useState(null);



    /* ============================= */
    /*            INIT               */
    /* ============================= */

    useEffect(() => {
        if (rootMobile) {
            setCurrentMobile(rootMobile);
            setPath([]);
        }
    }, [rootMobile]);

    useEffect(() => {
        if (currentMobile) fetchHierarchy(currentMobile);
    }, [currentMobile]);

    /* ============================= */
    /*        FETCH HIERARCHY        */
    /* ============================= */

    const fetchHierarchy = async (mobile) => {
        setLoading(true);
        setError("");

        try {
            const res = await hierarchy(
                null,
                `?mobile=${mobile}&fromDate=${fromDate}&toDate=${toDate}`
            );

            if (Array.isArray(res)) {
                setHierarchyData(res);  // ✅ STORE RESPONSE HERE
            } else {
                setHierarchyData([]);
            }

            setSelectedMid(null);
            setSelectedService(null);

        } catch (err) {
            setError("Failed to load hierarchy");
        } finally {
            setLoading(false);
        }
    };

    const amt = (v) => `₹${Number(v || 0).toFixed(2)}`;


    /* ============================= */
    /*         EXCEL DOWNLOAD        */
    /* ============================= */


    const downloadExcel = async () => {
        try {
            const filteredData = filteredHierarchy;

            if (!filteredData.length) {
                alert("No merchant data found");
                return;
            }

            const workbook = new ExcelJS.Workbook();
            const ws = workbook.addWorksheet("Hierarchy");

            const headers = [
                "SH", "ASM",
                "Master Distributor", "Distributor", "Retailer", "Independent Merchant",

                "T-DMT", "Y-DMT", "M-DMT",
                "T-AEPS", "Y-AEPS", "M-AEPS",
                "T-Recharge", "Y-Recharge", "M-Recharge",
                "T-Payout", "Y-Payout", "M-Payout",
                "T-CreditCard", "Y-CreditCard", "M-CreditCard",

                "T-Total", "Y-Total", "M-Total",
                "Main", "AEPS Balance", "Commission"
            ];

            /* ================= HEADER ================= */
            const headerRow = ws.addRow(headers);

            headerRow.eachCell((cell) => {
                cell.font = { bold: true, color: { argb: "FF000000" } };

                // 🟡 Yellow header
                cell.fill = {
                    type: "pattern",
                    pattern: "solid",
                    fgColor: { argb: "FFFFEB3B" }
                };

                cell.alignment = { horizontal: "center", vertical: "middle" };

                cell.border = {
                    top: { style: "thin" },
                    left: { style: "thin" },
                    bottom: { style: "thin" },
                    right: { style: "thin" }
                };
            });

            /* ================= DATA ================= */
            filteredData.forEach((row) => {
                ws.addRow([
                    row.sh || "",
                    row.asm || "",
                    row.md || "",
                    row.distributor || "",
                    row.merchant || "",
                    row.independentMerchant ||  "",

                    row.dmt || 0,
                    row.ydmt || 0,
                    row.mdmt || 0,

                    row.aeps || 0,
                    row.yaeps || 0,
                    row.maeps || 0,

                    row.recharge || 0,
                    row.yrecharge || 0,
                    row.mrecharge || 0,

                    row.payout || 0,
                    row.ypayout || 0,
                    row.mpayout || 0,

                    row.creditCard || 0,
                    row.ycreditCard || 0,
                    row.mcreditCard || 0,

                    row.total || 0,
                    row.yesterday || 0,
                    row.monthTotal || 0,

                    row.mainBalance || 0,
                    row.aepsBalanceWallet || 0,
                    row.commissionBalance || 0,
                ]);
            });

            /* ================= STYLING (SKIP HEADER) ================= */
            ws.eachRow((row, rowNumber) => {

                if (rowNumber === 1) return; // ❗ VERY IMPORTANT

                row.eachCell((cell, colNumber) => {

                    // 🔠 Bold all text
                    cell.font = { bold: true };

                    // 📦 Borders
                    cell.border = {
                        top: { style: "thin" },
                        left: { style: "thin" },
                        bottom: { style: "thin" },
                        right: { style: "thin" }
                    };

                    // 🎨 Section Colors

                    // NH → ASM
                    if (colNumber <= 2) {
                        cell.fill = {
                            type: "pattern",
                            pattern: "solid",
                            fgColor: { argb: "FFE3F2FD" }
                        };
                    }

                    // Distributor → Merchant
                    else if (colNumber <= 6) {
                        cell.fill = {
                            type: "pattern",
                            pattern: "solid",
                            fgColor: { argb: "FFE8F5E9" }
                        };
                    }

                    // Transactions
                    else if (colNumber <= 21) {
                        cell.fill = {
                            type: "pattern",
                            pattern: "solid",
                            fgColor: { argb: "FFFFF3E0" }
                        };
                    }

                    // Totals / Balance
                    else {
                        cell.fill = {
                            type: "pattern",
                            pattern: "solid",
                            fgColor: { argb: "FFFFCDD2" }
                        };
                    }

                    // 💰 Right align numbers
                    if (typeof cell.value === "number") {
                        cell.alignment = { horizontal: "right" };
                    }
                });
            });

            /* ================= WIDTH ================= */
            ws.columns.forEach(col => col.width = 20);

            /* ================= FREEZE HEADER ================= */
            ws.views = [{ state: "frozen", ySplit: 1 }];

            const buffer = await workbook.xlsx.writeBuffer();

            saveAs(
                new Blob([buffer]),
                `Hierarchy_${fromDate}.xlsx`
            );

        } catch (err) {
            console.error(err);
            alert("Excel download failed");
        }
    };


    const filteredHierarchy = hierarchyData.filter((row) => {

        if (!row.leaf) return false;

        const searchText = search.toLowerCase();

        return (
            row.sh?.toLowerCase().includes(searchText) ||
            row.asm?.toLowerCase().includes(searchText) ||
            row.md?.toLowerCase().includes(searchText) ||
            row.distributor?.toLowerCase().includes(searchText) ||
            row.merchant?.toLowerCase().includes(searchText) ||
            row.nameWithMobile?.toLowerCase().includes(searchText)
        );
    });

    /* ============================= */
    /*            RENDER             */
    /* ============================= */

    return (
        <div className="p-6 max-w-[1800px] mx-auto space-y-5">

            {/* HEADER */}
            <div className="bg-white rounded-2xl shadow border p-5 flex flex-wrap gap-4 justify-between items-end">
                <div>
                    <h1 className="text-xl font-bold">Hierarchy</h1>
                </div>

                <input
                    type="text"
                    placeholder="Search by Name / Mobile..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="border px-3 py-2 rounded-lg text-sm w-[250px]"
                />

                <div className="flex gap-3 flex-wrap items-end">
                    <div>
                        <label className="block text-xs mb-1">From</label>
                        <input
                            type="date"
                            value={fromDate}
                            min={MIN_DATE}
                            max={MAX_DATE}
                            onChange={(e) => {
                                const v = e.target.value;
                                setFromDate(v);
                                setToDate(v); // 👈 always same
                            }}
                        />
                    </div>

                    <div>
                        <label className="block text-xs mb-1">To</label>
                        <input
                            type="date"
                            value={toDate}
                            disabled
                        />
                    </div>

                    <button
                        onClick={() => fetchHierarchy(currentMobile)}
                        className="h-[42px] px-6 rounded-lg bg-indigo-600 text-white"
                    >
                        Search
                    </button>

                    <button
                        onClick={downloadExcel}
                        className="h-[42px] px-6 rounded-lg bg-green-600 text-white"
                    >
                        Download Excel
                    </button>
                </div>
            </div>

            {loading && filteredHierarchy.length === 0 && (
                <div className="bg-white rounded-2xl shadow border p-10 flex justify-center items-center">
                    <div className="flex flex-col items-center gap-3">
                        <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
                        <p className="text-sm text-gray-700 font-medium">
                            Loading hierarchy...
                        </p>
                    </div>
                </div>
            )}

            {/* TABLE */}
            {!loading && filteredHierarchy.length > 0 && (
                <div className="w-full overflow-auto max-h-[75vh] border rounded-xl shadow-sm">
                    <table className="w-full text-sm border-collapse">

                        {/* ✅ Sticky Header */}
                        <thead className="sticky top-0 z-20 bg-gradient-to-r from-slate-800 to-slate-700 text-white">
                            <tr>
                                {HIERARCHY_COLUMNS.map((h) => (
                                    <th
                                        key={h}
                                        className="px-4 py-3 font-semibold text-left border border-slate-600 whitespace-nowrap"
                                    >
                                        {h}
                                    </th>
                                ))}

                                {[
                                    "T-DMT", "Y-DMT", "M-DMT",
                                    "T-AEPS", "Y-AEPS", "M-AEPS",
                                    "T-Recharge", "Y-Recharge", "M-Recharge",
                                    "T-Payout", "Y-Payout", "M-Payout",
                                    "T-Credit", "Y-Credit", "M-Credit",
                                    "T-Total", "Y-Total", "M-Total",
                                    "Main", "AEPS", "Commission"
                                ].map((h) => (
                                    <th
                                        key={h}
                                        className="px-4 py-3 font-semibold text-right border border-slate-600 whitespace-nowrap"
                                    >
                                        {h}
                                    </th>
                                ))}
                            </tr>
                        </thead>

                        {/* ✅ Body */}
                        <tbody>
                            {filteredHierarchy
                                .filter(row => row.leaf === true)
                                .map((row, i) => (
                                    <tr
                                        key={i}
                                        className={`transition-all duration-200 ${i % 2 === 0 ? "bg-white" : "bg-slate-50"
                                            } hover:bg-blue-50`}
                                    >

                                        {/* 🔥 Hierarchy Columns */}
                                        <td className="px-4 py-2 border border-gray-300 font-semibold text-yellow-600">{row.sh}</td>
                                        <td className="px-4 py-2 border border-gray-300 font-semibold text-orange-600">{row.asm}</td>
                                        <td className="px-4 py-2 border border-gray-300 font-semibold text-cyan-600">{row.md}</td>
                                        <td className="px-4 py-2 border border-gray-300 font-semibold text-lime-600">{row.distributor}</td>
                                        <td className="px-4 py-2 border border-gray-300 font-semibold text-gray-800">{row.merchant}</td>
                                        <td className="px-4 py-2 border border-gray-300 font-semibold text-pink-600">{row.independentMerchant}</td>

                                        {/* 🔢 Amount Columns */}
                                        <td className="px-4 py-2 text-right border border-gray-300">{amt(row.dmt)}</td>
                                        <td className="px-4 py-2 text-right border border-gray-300">{amt(row.ydmt)}</td>
                                        <td className="px-4 py-2 text-right border border-gray-300">{amt(row.mdmt)}</td>

                                        <td className="px-4 py-2 text-right border border-gray-300">{amt(row.aeps)}</td>
                                        <td className="px-4 py-2 text-right border border-gray-300">{amt(row.yaeps)}</td>
                                        <td className="px-4 py-2 text-right border border-gray-300">{amt(row.maeps)}</td>

                                        <td className="px-4 py-2 text-right border border-gray-300">{amt(row.recharge)}</td>
                                        <td className="px-4 py-2 text-right border border-gray-300">{amt(row.yrecharge)}</td>
                                        <td className="px-4 py-2 text-right border border-gray-300">{amt(row.mrecharge)}</td>

                                        <td className="px-4 py-2 text-right border border-gray-300">{amt(row.payout)}</td>
                                        <td className="px-4 py-2 text-right border border-gray-300">{amt(row.ypayout)}</td>
                                        <td className="px-4 py-2 text-right border border-gray-300">{amt(row.mpayout)}</td>

                                        <td className="px-4 py-2 text-right border border-gray-300">{amt(row.creditCard)}</td>
                                        <td className="px-4 py-2 text-right border border-gray-300">{amt(row.ycreditCard)}</td>
                                        <td className="px-4 py-2 text-right border border-gray-300">{amt(row.mcreditCard)}</td>

                                        {/* ⭐ Highlight */}
                                        <td className="px-4 py-2 text-right font-bold text-green-600 border border-gray-300">
                                            {amt(row.total)}
                                        </td>

                                        <td className="px-4 py-2 text-right border border-gray-300">{amt(row.yesterday)}</td>
                                        <td className="px-4 py-2 text-right border border-gray-300">{amt(row.monthTotal)}</td>

                                        <td className="px-4 py-2 text-right border border-gray-300 font-medium text-blue-600">
                                            {amt(row.mainBalance)}
                                        </td>
                                        <td className="px-4 py-2 text-right border border-gray-300 font-medium text-purple-600">
                                            {amt(row.aepsBalanceWallet)}
                                        </td>
                                        <td className="px-4 py-2 text-right border border-gray-300 font-medium text-orange-600">
                                            {amt(row.commissionBalance)}
                                        </td>

                                    </tr>
                                ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* MODAL */}
            <Modal
                open={Boolean(selectedMid && selectedService)}
                onClose={() => {
                    setSelectedMid(null);
                    setSelectedService(null);
                }}
                title="Merchant Transaction History"
            >
                <MerchantShTxnHistory
                    mid={selectedMid}
                    service={selectedService}
                />
            </Modal>
        </div>
    );
};

export default HierarchyShshow;














